import React from 'react'
import { Modal, Progress, Collapse, Table } from 'antd'
import { confirmBase, errorBase, PasswordInput } from 'utils/ui'
import { openFile } from 'utils'
import { isJP, isUS } from 'conf'
import { QUOTA_MODE } from 'types/utils'
import { getTempEnvParam } from 'env'
import { store as reduxStore } from 'index'
import ResourceService from 'api/Resource'
import { featureFlags } from 'config/feature-flags'
import ResourceTemplateService from 'api/ResourceTemplate'
import UserService from 'api/Users'
import history from 'history'
import i18n from 'i18next'
import classNames from 'classnames'
import {
  getEmailItems,
  sendDarkWebService,
  getProcessEmailList,
  checkLeakedRecord,
  getZdwExpiredAt,
} from 'dUtils'
import { uploadFilesToS3, mapUploadResultsToSendData } from 'utils/importExport/utils/s3Upload'
import {
  makeProgressContent,
  formatElapsed,
  computeSHA256Hex,
  createSemaphore,
  getMaxRecordFileSize,
  HEAVY_SIZE_THRESHOLD,
} from 'utils/importExport/bulkExport'
import {
  parseCsvRaw,
  buildAutoMapping,
  convertWithMapping,
  flattenExternalJson,
  isSafeboxBackup,
} from 'utils/importExport/externalImport'
import exclamationBlackIcon from '@/assets/images/exclamation-circle-black-16.svg'
import exclamationRedIcon from '@/assets/images/exclamation-circle-red-22.svg'
import styles from './style.module.scss'

/**
 * Import 에러 팝업 (Figma 디자인 기준)
 * 빨간 느낌표 아이콘 + "Invalid file format" 제목 + 설명 + OK 버튼
 */
const showImportErrorModal = (t) => {
  confirmBase({
    wrapClassName: classNames(styles.importErrorModal),
    width: 480,
    icon: <img src={exclamationRedIcon} alt="error" width={22} height={22} />,
    title: <div className={styles.importErrorTitle}>{t('record.error.invalidFileFormatTitle')}</div>,
    content: (
      <div className={styles.importErrorContent}>
        {t('record.error.invalidFileFormatDesc')}
      </div>
    ),
    okText: t('common.button.ok'),
    okButtonProps: { className: classNames(styles.importErrorBtnOk) },
    cancelButtonProps: { style: { display: 'none' } },
  })
}

/**
 * 비밀번호 가져오기 실패 전용 모달 (v2 Figma 기획)
 * 파일 파싱 / 복호화 / 매핑 변환 / 템플릿 조회 / API 호출 등 모든 Import 단계의 에러에 대해 사용.
 *
 * @param {Function} t - i18next 번역 함수
 * @param {Object} [options]
 * @param {string} [options.customMessage] - 기본 "파일을 확인해주세요" 대신 표시할 커스텀 메시지 (v2.6 추가)
 */
const showImportFailedModal = (t, { customMessage = null } = {}) => {
  confirmBase({
    wrapClassName: classNames(styles.importErrorModal),
    width: 480,
    icon: <img src={exclamationRedIcon} alt="error" width={22} height={22} />,
    title: <div className={styles.importErrorTitle}>{t('record.import.v2.error.title')}</div>,
    content: (
      <div className={styles.importErrorContent}>
        {customMessage || t('record.import.v2.error.message')}
      </div>
    ),
    okText: t('common.button.ok'),
    okButtonProps: { className: classNames(styles.importErrorBtnOk) },
    cancelButtonProps: { style: { display: 'none' } },
  })
}

const FILE_SIZE_LIMIT = null // 10MB limit

const showImportResultModal = (content, t, { openItem = false, elapsedMs = null } = {}) => {
  const isDev = process.env.REACT_APP_TARGET !== 'production'
  const modalContent =
    isDev && elapsedMs != null ? (
      <div>
        <div>{content}</div>
        <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
          소요 시간: {formatElapsed(elapsedMs)} (개발버전에서만 표시)
        </div>
      </div>
    ) : (
      content
    )

  confirmBase({
    wrapClassName: classNames(styles.decryptModal),
    icon: <img src={exclamationBlackIcon} alt="info" width={22} height={22} />,
    title: <div className={styles.decryptTitle}>{t('record.title.importCompleted')}</div>,
    content: modalContent,
    okText: t('common.button.ok'),
    okButtonProps: { className: classNames('type7', 'w100', 'h38') },
    cancelButtonProps: { style: { display: 'none' } },
    onOk: () => {
      const tablePage = getTempEnvParam('curTablePage')

      if (tablePage && tablePage.reqData) {
        tablePage.reqData(null)
      }

      if (openItem && tablePage && tablePage.openItem) {
        tablePage.openItem(null)
      } else if (!tablePage?.reqData) {
        history.push('/records/all')
      }
    },
  })
}

const rejectKeepOpen = () => {
  const p = Promise.reject()
  p.catch(() => {})
  return p
}

const validateImportRecord = (data) => {
  return data && data.title && Array.isArray(data.items)
}

const unwrapExportData = (data) => {
  if (data && data.version && Array.isArray(data.records)) {
    return data.records
  }
  return data
}

const buildImportItems = async (importData, rsKeyObj, computeEtag) => {
  let cnt = -1
  const getOrder = () => {
    cnt += 1
    return cnt
  }

  const files = []

  const items = await Promise.all(
    importData.items.map(async (item) => {
      const field = {
        type: (item.type || '').toLowerCase(),
        label: item.label,
        custom: item.custom === true,
        required: item.required || false,
        encrypt: item.encrypt || false,
        order: getOrder() + 1,
      }

      if (item.type === 'files' && Array.isArray(item.content)) {
        const fileContents = await Promise.all(
          item.content.map(async (fileItem) => {
            const fileField = {
              type: 'file',
              encrypt: fileItem.encrypt || false,
              filename: fileItem.filename,
              size: fileItem.size,
              content_type: fileItem.content_type,
              order: getOrder() + 1,
            }

            if (fileItem.data) {
              const rawData = Array.isArray(fileItem.data) ? fileItem.data.join('') : fileItem.data
              const binaryStr = atob(rawData)
              const originBytes = Uint8Array.from(binaryStr, (c) => c.charCodeAt(0))

              if (fileItem.encrypt) {
                const encrypted = await rsKeyObj.encryptBytes(originBytes.buffer)
                if (computeEtag) fileField.etag = rsKeyObj.md5(encrypted)
                const encryptedBytes = Uint8Array.from(encrypted, (c) => c.charCodeAt(0))
                files.push(
                  new File([encryptedBytes], fileItem.filename, { type: fileItem.content_type }),
                )
              } else {
                if (computeEtag) fileField.etag = rsKeyObj.md5(binaryStr)
                files.push(
                  new File([originBytes], fileItem.filename, { type: fileItem.content_type }),
                )
              }
            }

            return fileField
          }),
        )
        field.content = fileContents
      } else {
        let { content } = item
        if (item.encrypt === true && content != null && content !== '') {
          content = await rsKeyObj.encrypt(String(content))
        }
        field.content = content
      }

      return field
    }),
  )

  return { items, files }
}

const buildSendData = (importData, rsKeyObj, items) => ({
  template: { id: importData.template?.id },
  key: {
    enc_user_rs_key: rsKeyObj.getEncRsKey(),
    expired_at: new Date().toISOString(),
  },
  title: importData.title,
  items,
})

const buildImportSendData = async (importData, rsKeyObj) => {
  const { items, files } = await buildImportItems(importData, rsKeyObj, false)
  return { sendData: buildSendData(importData, rsKeyObj, items), files }
}

const buildImportFormData = async (importData, rsKeyObj) => {
  const { items, files } = await buildImportItems(importData, rsKeyObj, true)
  const sendData = buildSendData(importData, rsKeyObj, items)
  const formData = new FormData()
  formData.append('data', JSON.stringify(sendData))
  files.forEach((file) => formData.append('files', file))
  return formData
}

const executeImportRecord = async (importData) => {
  const rsaEncDec = getTempEnvParam('rsaObj')
  const rsKeyObj = rsaEncDec.createRSKeyObj()

  if (featureFlags.usePresignedUpload) {
    const { sendData, files } = await buildImportSendData(importData, rsKeyObj)

    if (files.length > 0) {
      const fileMeta = files.map((file) => ({
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        size: file.size,
      }))
      const urlResponse = await ResourceService.getUploadUrls(fileMeta)
      const { resource_id: presignedResourceId, files: uploadFiles } = urlResponse.data
      const uploadResults = await uploadFilesToS3(files, uploadFiles)
      mapUploadResultsToSendData(sendData, uploadResults)
      sendData.resource_id = presignedResourceId
    }

    if (featureFlags.dryRun) return { success: true, resourceId: null }

    const response = await ResourceService.createPresigned(sendData, { skip_activity: true })
    if (response.code === 795 || response.code === 599) {
      const err = new Error('QUOTA_EXCEEDED')
      err.code = response.code
      throw err
    }
    return { success: response.code === 200, resourceId: response.data?.resource_id }
  }

  if (featureFlags.dryRun) return { success: true, resourceId: null }

  const formData = await buildImportFormData(importData, rsKeyObj)
  const response = await ResourceService.create(formData)

  if (response.code === 795 || response.code === 599) {
    const err = new Error('QUOTA_EXCEEDED')
    err.code = response.code
    throw err
  }
  return { success: response.code === 200, resourceId: response.data?.resource_id }
}

// 다수의 레코드 생성 (Worker Pool 방식)
const executeImportRecords = async (records, onProgress) => {
  const maxRecordFileSize = getMaxRecordFileSize(records)
  const RECORD_CONCURRENCY = maxRecordFileSize > HEAVY_SIZE_THRESHOLD ? 1 : 3

  const semaphore = createSemaphore(RECORD_CONCURRENCY)
  let successCount = 0
  let failCount = 0
  const importedResourceIds = []
  const failedRecords = []
  let completed = 0
  let quotaExceeded = false

  await Promise.all(
    records.map(async (record) => {
      if (quotaExceeded) return

      await semaphore.acquire()
      try {
        if (quotaExceeded) return

        const result = await executeImportRecord(record)
        if (result.success) {
          successCount += 1
          importedResourceIds.push(result.resourceId)
        } else {
          failCount += 1
          failedRecords.push({ title: record.title || '', reason: 'api_error' })
        }
      } catch (error) {
        if (error?.code === 795 || error?.code === 599) {
          quotaExceeded = true
        } else {
          console.error(`Import record failed:`, error)
          failCount += 1
          failedRecords.push({ title: record.title || '', reason: 'api_error' })
        }
      } finally {
        semaphore.release()
        completed += 1
        onProgress?.(completed, records.length)
      }
    }),
  )

  return { successCount, failCount, importedResourceIds, failedRecords, quotaExceeded }
}

const reloadMenu = () => {
  reduxStore.dispatch({ type: 'menu/RELOAD' })
}

// import 후 다크웹 체크 수행
// onProgress(current, total): 레코드별 진행률 콜백 (optional)
const handleDarkWebCheckAfterImport = async (resourceIds, onProgress) => {
  try {
    const zdwActive = reduxStore.getState().settings.zeroDarkWebSettings?.zdw_active
    if (zdwActive !== true && zdwActive !== 'true') return

    if (!resourceIds || resourceIds.length === 0) return

    const { locale } = reduxStore.getState().settings
    const { requestEmailItems, resourceItems } = await getEmailItems(null, locale, resourceIds)

    if (requestEmailItems.length === 0) return

    const total = resourceItems.length
    onProgress?.(0, total)

    const response = await sendDarkWebService(requestEmailItems)
    if (response?.code !== 200) {
      console.error('❌ Error fetching DarkWeb data:', response.message)
      return
    }

    const processedEmailList = getProcessEmailList(response?.results)
    const processedRecordList = []

    for (let i = 0; i < resourceItems.length; i += 1) {
      const item = resourceItems[i]
      const isLeaked = checkLeakedRecord(item, processedEmailList)
      if (isLeaked) {
        processedRecordList.push({
          resource_id: item.resourceId,
          is_leaked: true,
          leaked_content: isLeaked,
        })
      } else if (isLeaked === false && item.isLeaked === true) {
        processedRecordList.push({ resource_id: item.resourceId, is_leaked: false })
      }
      onProgress?.(i + 1, total)
    }

    if (processedRecordList.length > 0) {
      const request = { resources: processedRecordList }
      const response1 = await ResourceService.bulkPutLeaked(request)
      if (response1?.code !== 200) {
        console.error('❌ Error putting leaked data:', response1.message)
        return
      }
    }

    reduxStore.dispatch({ type: 'user/SET_LEAKED_RECORD_COUNT' })
    reduxStore.dispatch({
      type: 'settings/SET_SETTINGS',
      payload: {
        zeroDarkWebSettings: {
          zdw_checked_at: new Date().toISOString(),
          zdw_expired_at: getZdwExpiredAt(),
        },
      },
    })
  } catch (error) {
    console.error('❌ DarkWeb check after import failed:', error)
  }
}

const FORCE_DARK_WEB_UI_TEST = false

const isDarkWebActive = () => {
  if (FORCE_DARK_WEB_UI_TEST) return true
  const zdwActive = reduxStore.getState().settings.zeroDarkWebSettings?.zdw_active
  return zdwActive === true || zdwActive === 'true'
}

const makeDarkWebContent = (t, current, total) => (
  <div className={styles.progressModal}>
    <Progress
      percent={total > 0 ? Math.round((current / total) * 100) : 0}
      status={current < total ? 'active' : 'success'}
    />
    <div className={styles.progressCount}>
      {current} / {total}
    </div>
    <div className={styles.progressWarning}>{t('topBar.bulkExport.progressWarning')}</div>
  </div>
)

const runDarkWebCheckWithAnimation = async (resourceIds, onUpdate, t) => {
  const total = resourceIds.length || 1

  if (FORCE_DARK_WEB_UI_TEST) {
    const state = { fakeCount: 0 }
    onUpdate(makeDarkWebContent(t, 0, total))

    await new Promise((resolve) => {
      const interval = setInterval(() => {
        if (state.fakeCount < total - 1) {
          state.fakeCount += 1
          onUpdate(makeDarkWebContent(t, state.fakeCount, total))
        } else {
          clearInterval(interval)
          resolve()
        }
      }, 1000)
    })

    onUpdate(makeDarkWebContent(t, total, total))

    return
  }

  let displayCount = 0

  onUpdate(makeDarkWebContent(t, 0, total))
  const interval = setInterval(() => {
    const next = Math.min(displayCount + 3, total - 1)
    if (next > displayCount) {
      displayCount = next
      onUpdate(makeDarkWebContent(t, displayCount, total))
    }
  }, 1000)

  await handleDarkWebCheckAfterImport(resourceIds)

  clearInterval(interval)

  onUpdate(makeDarkWebContent(t, total, total))
}

const showDarkWebModal = (t, total) =>
  Modal.confirm({
    title: t('record.result.darkWebUpdating'),
    icon: null,
    width: 480,
    content: makeDarkWebContent(t, 0, total),
    closable: false,
    keyboard: false,
    maskClosable: false,
    okButtonProps: { style: { display: 'none' } },
    cancelButtonProps: { style: { display: 'none' } },
    wrapClassName: classNames('om-popup-1', styles.progressModalWrapper),
  })

const handleSingleImport = async (importData, t, messageApi) => {
  const startTime = Date.now()

  messageApi.loading({
    content: t('record.result.importLoading'),
    key: 'importLoading',
    duration: 0,
  })

  try {
    const result = await executeImportRecord(importData)
    messageApi.destroy('importLoading')

    if (result.success) {
      reloadMenu()
      await handleDarkWebCheckAfterImport([result.resourceId])
      showImportResultModal(t('record.result.importSuccess'), t, {
        openItem: true,
        elapsedMs: Date.now() - startTime,
      })
    } else {
      messageApi.error(t('record.error.importFailed'))
    }
  } catch (error) {
    messageApi.destroy('importLoading')
    if (error?.code === 795 || error?.code === 599) {
      errorBase({
        title: t('record.title.maxLimitReached'),
        content: t('record.result.importQuotaExceeded', { count: 0 }),
        okText: t('common.button.ok'),
      })
    } else {
      console.error('Single import failed:', error)
      messageApi.error(t('record.error.importFailed'))
    }
  }
}

const makeDetailContent = (successCount, failCount, skipCount, failedRows, t) => {
  const hasDetails = failedRows.length > 0

  let summary
  if (skipCount > 0) {
    summary = t('record.result.importSkipped', { success: successCount, fail: failCount, skip: skipCount })
  } else if (failCount > 0) {
    summary = t('record.result.importPartialFail', { success: successCount, fail: failCount })
  } else {
    summary = t('record.result.importSuccessMultiple', { count: successCount })
  }

  if (!hasDetails) return summary

  const columns = [
    { title: t('record.result.importDetailRow'), dataIndex: 'row', width: 60 },
    { title: t('record.result.importDetailTitle'), dataIndex: 'title', render: (val) => val || t('record.result.importReasonNoTitle') },
    {
      title: t('record.result.importDetailReason'),
      dataIndex: 'reason',
      render: (val) => {
        const reasonMap = {
          parse_error: t('record.result.importReasonParseError'),
          unsupported_type: t('record.result.importReasonUnsupportedType'),
          api_error: t('record.result.importReasonApiError'),
        }
        return reasonMap[val] || val
      },
    },
  ]

  return (
    <div>
      <div>{summary}</div>
      <Collapse ghost style={{ marginTop: 8, padding: 0 }}>
        <Collapse.Panel header={t('record.result.importDetailToggle')} key="detail">
          <Table
            size="small"
            pagination={false}
            dataSource={failedRows.map((r, i) => ({ ...r, key: i }))}
            columns={columns}
          />
        </Collapse.Panel>
      </Collapse>
    </div>
  )
}

const handleMultipleImport = async (records, t, { skippedRows = [] } = {}) => {
  const startTime = Date.now()

  const progressModal = Modal.confirm({
    title: t('record.result.importLoading'),
    icon: null,
    width: 480,
    content: makeProgressContent(0, records.length, t),
    closable: false,
    keyboard: false,
    maskClosable: false,
    okButtonProps: { style: { display: 'none' } },
    cancelButtonProps: { style: { display: 'none' } },
    wrapClassName: classNames('om-popup-1', styles.progressModalWrapper),
  })

  const { successCount, failCount, importedResourceIds, failedRecords, quotaExceeded } =
    await executeImportRecords(records, (current, total) =>
      progressModal.update({ content: makeProgressContent(current, total, t) }),
    )

  if (quotaExceeded) {
    progressModal.destroy()
    errorBase({
      title: t('record.title.maxLimitReached'),
      content: t('record.result.importQuotaExceeded', { count: successCount }),
      okText: t('common.button.ok'),
    })

    const tablePage = getTempEnvParam('curTablePage')
    if (tablePage?.reqData) tablePage.reqData(null)

    if (successCount === 0) return
  }

  reloadMenu()

  const validImportedIds = importedResourceIds.filter((id) => id != null)
  if (isDarkWebActive() && validImportedIds.length > 1) {
    progressModal.destroy()
    const dwModal = showDarkWebModal(t, validImportedIds.length)

    await runDarkWebCheckWithAnimation(
      validImportedIds,
      (content) => dwModal.update({ content }),
      t,
    )
    dwModal.destroy()
  } else {
    progressModal.destroy()
    await handleDarkWebCheckAfterImport(importedResourceIds)
  }

  // 파서 단계의 skippedRows + API 단계의 failedRecords 합산
  const allFailedRows = [
    ...skippedRows.map((r) => ({ ...r, reason: r.reason || 'parse_error' })),
    ...failedRecords.map((r) => ({ ...r, row: '-' })),
  ]
  const skipCount = skippedRows.length

  let resultContent
  if (failCount === 0 && skipCount === 0) {
    resultContent = t('record.result.importSuccessMultiple', { count: successCount })
  } else if (successCount === 0 && failCount > 0) {
    resultContent = t('record.error.importFailed')
  } else {
    resultContent = makeDetailContent(successCount, failCount, skipCount, allFailedRows, t)
  }

  if (!quotaExceeded) showImportResultModal(resultContent, t, { elapsedMs: Date.now() - startTime })
}

const fetchDefaultTemplateId = async (messageApi, t) => {
  try {
    // search 파라미터로 "Default" 검색
    const response = await ResourceTemplateService.getAll({
      params: {
        search: 'Default',
        offset: 0,
        limit: 10,
      },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    // readonly: true인 템플릿 찾기
    const defaultTemplate = response.data.find((template) => template.readonly === true)

    if (!defaultTemplate) {
      messageApi.error(t('record.error.defaultTemplateNotFound'))
      return null
    }

    return defaultTemplate.id
  } catch (error) {
    console.error('Failed to fetch default template:', error)
    messageApi.error(t('record.error.templateFetchFailed'))
    return null
  }
}

const validateTemplateId = async (templateId) => {
  if (!templateId) return false

  try {
    const response = await ResourceTemplateService.get(templateId)
    return response.code === 200
  } catch (error) {
    console.warn(`Template ID ${templateId} validation failed:`, error)
    return false
  }
}

/**
 * 템플릿 ID로 상세 스키마를 조회하고 파싱한다. (v2.3)
 *
 * ResourceTemplate API 응답의 `content`는 JSON 문자열이며, 파싱하면
 * `base_schema.ui_widgets` (필드 목록) + `base_schema.required_uids` (필수 필드의 uid)를 포함한다.
 * translations로 영문 label을 locale별 한글 라벨로 변환한다.
 *
 * @param {string} templateId
 * @returns {Promise<{
 *   templateName: string,
 *   translations: Object,
 *   fields: Array<{ uid, label, labelLocalized, type, required }>,
 * }>}
 */
const fetchTemplateSchema = async (templateId) => {
  const response = await ResourceTemplateService.get(templateId)
  if (response.code !== 200 || !response.data?.content) {
    throw new Error('TEMPLATE_FETCH_FAILED')
  }

  const content = JSON.parse(response.data.content)
  const baseSchema = content?.base_schema || {}
  const translations = content?.translations || {}
  const uiWidgets = baseSchema.ui_widgets || []
  const requiredUids = baseSchema.required_uids || []

  // i18n.language 예: "ko-KR" → Figma translations 키 "ko_KR"
  const locale = (i18n.language || 'ko-KR').replace('-', '_')

  return {
    templateName: response.data.name,
    translations,
    fields: uiWidgets.map((w) => ({
      uid: w.uid,
      label: w.label,
      labelLocalized: translations[locale]?.[w.label] || w.label,
      type: w.type,
      required: requiredUids.includes(w.uid),
    })),
  }
}

const toCustomFields = (record) => {
  // Default 템플릿에는 "메모" 필드(type: "note")가 기본으로 있음
  if (!record.items || !Array.isArray(record.items)) return record

  return {
    ...record,
    items: record.items.map((item) => ({
      ...item,
      // type이 "note"인 경우 custom: false 유지, 나머지는 custom: true
      custom: item.type !== 'note',
    })),
  }
}

/**
 * 템플릿 data_types 배열 조회 (v2.7).
 *
 * helper.js의 templateCache와는 독립된 모듈 레벨 캐시. Import 모달 내 검증에서만 사용.
 * 구조: Array<{ type_name, format, min_length, max_length, encrypt, active, ... }>
 * 응답 오류 시 빈 배열 반환 → 검증은 타입 규칙만 적용 (minLength 규칙 skip).
 */
let typeListCache = null
let typeListPromise = null

const fetchTypeList = async () => {
  if (typeListCache) return typeListCache
  if (typeListPromise) return typeListPromise
  typeListPromise = ResourceTemplateService.getAll()
    .then((res) => {
      typeListPromise = null
      if (res?.code === 200 && Array.isArray(res.data_types)) {
        typeListCache = res.data_types
        return typeListCache
      }
      return []
    })
    .catch(() => {
      typeListPromise = null
      return []
    })
  return typeListPromise
}

const checkImportQuota = async (recordCount, t) => {
  const { paidLicense } = reduxStore.getState().user
  if ((!isJP() && !isUS()) || paidLicense?.quota_mode !== QUOTA_MODE.RESOURCE) return true

  try {
    const res = await UserService.getMyResourceQuota()
    const { user_quota: userQuota } = res.data

    if (!userQuota?.max_resources) return true

    const remainingQuota = userQuota.max_resources - userQuota.current_resources
    if (recordCount > remainingQuota) {
      errorBase({
        icon: <img src={exclamationRedIcon} alt="exclamation" />,
        title: t('record.title.importQuotaExceeded'),
        content: t('record.desc.importQuotaExceeded'),
        width: 480,
        okText: t('common.button.ok'),
        okButtonProps: { className: classNames('type7', 'w100', 'h38') },
        cancelText: t('common.button.cancel'),
        onOk: () => {},
      })
      return false
    }

    return true
  } catch (error) {
    console.error('Error checking import quota:', error)
    return true
  }
}

const decryptRecords = (encRecords, password) =>
  new Promise((resolve, reject) => {
    const CONCURRENCY = 10
    const results = new Array(encRecords.length)
    let started = 0
    let completed = 0

    const run = () => {
      if (started === encRecords.length) return
      const index = started
      started += 1

      decryptForImport(encRecords[index], password)
        .then((str) => {
          results[index] = JSON.parse(str)
          completed += 1
          if (completed === encRecords.length) resolve(results)
          else run()
        })
        .catch(reject)
    }

    Array.from({ length: Math.min(CONCURRENCY, encRecords.length) }, run)
  })

const processImportData = async (importData, t, messageApi, { encryptedData, password, skippedRows } = {}) => {
  let resolvedData = importData

  if (encryptedData && password) {
    const { records: encRecords, checksum } = encryptedData

    const flatRecords = encRecords.map((r) => (Array.isArray(r) ? r.join('') : r))
    const expected = await computeSHA256Hex(flatRecords.join(''))
    if (!checksum) throw new Error(IMPORT_ERROR.CHECKSUM_MISSING)
    if (expected !== checksum) throw new Error(IMPORT_ERROR.CHECKSUM_MISMATCH)

    resolvedData = await decryptRecords(flatRecords, password)
  }

  const recordCount = Array.isArray(resolvedData) ? resolvedData.length : 1
  const canImport = await checkImportQuota(recordCount, t)
  if (!canImport) return

  // Promise를 캐싱해 동일 template.id는 병렬 실행 중에도 API를 한 번만 호출
  const templateValidationCache = new Map()
  const checkTemplateValid = (templateId) => {
    if (!templateValidationCache.has(templateId)) {
      templateValidationCache.set(templateId, validateTemplateId(templateId))
    }
    return templateValidationCache.get(templateId)
  }

  // default template ID도 캐싱하여 API 호출 최소화
  let cachedDefaultTemplateId = null
  const getDefaultTemplateId = async () => {
    if (cachedDefaultTemplateId) return cachedDefaultTemplateId

    const templateId = await fetchDefaultTemplateId(messageApi, t)
    if (!templateId) return null

    cachedDefaultTemplateId = templateId

    return cachedDefaultTemplateId
  }

  if (Array.isArray(resolvedData)) {
    // resolvedData가 없을 시 에러 처리 (템플릿이 없는 빈 파일 업로드한 경우 등)
    if (resolvedData.length === 0) {
      messageApi.error(t('record.error.importNoRecords'))
      return
    }

    const invalidIdx = resolvedData.findIndex((item) => !validateImportRecord(item))
    if (invalidIdx >= 0) {
      messageApi.error(t('record.error.invalidImportFile'))
      return
    }

    const validationResults = await Promise.all(
      resolvedData.map(async (record) => {
        const hasTemplateId = !!record.template?.id
        const isValidTemplate = hasTemplateId ? await checkTemplateValid(record.template.id) : false
        return { hasTemplateId, isValidTemplate }
      }),
    )

    // Default 템플릿이 필요한지 먼저 확인
    const needsDefaultTemplate = validationResults.some(
      (result) => !result.hasTemplateId || !result.isValidTemplate,
    )

    let defaultTemplateId = null
    if (needsDefaultTemplate) {
      defaultTemplateId = await getDefaultTemplateId()
      if (!defaultTemplateId) return
    }

    for (let i = 0; i < resolvedData.length; i += 1) {
      const { hasTemplateId, isValidTemplate } = validationResults[i]

      if (!hasTemplateId || !isValidTemplate) {
        resolvedData[i] = toCustomFields(resolvedData[i])
        resolvedData[i].template = { id: defaultTemplateId }
      }
    }

    await handleMultipleImport(resolvedData, t, { skippedRows: skippedRows || [] })
  } else {
    if (!validateImportRecord(resolvedData)) {
      messageApi.error(t('record.error.invalidImportFile'))
      return
    }

    const hasTemplateId = !!resolvedData.template?.id
    const isValidTemplate = hasTemplateId
      ? await checkTemplateValid(resolvedData.template.id)
      : false

    if (!hasTemplateId || !isValidTemplate) {
      const defaultTemplateId = await getDefaultTemplateId()
      if (!defaultTemplateId) return

      resolvedData = toCustomFields(resolvedData)
      resolvedData.template = { id: defaultTemplateId }
    }

    await handleSingleImport(resolvedData, t, messageApi)
  }
}

const IMPORT_ERROR = {
  CHECKSUM_MISSING: 'checksum_missing',
  CHECKSUM_MISMATCH: 'checksum_mismatch',
}

// encryptForExport(bulkExport.js)와 대칭되는 Web Crypto 기반 복호화
// 포맷: base64(salt(16) + IV(16) + AES-CBC-ciphertext)
const decryptForImport = async (base64Str, password) => {
  const combined = Uint8Array.from(atob(base64Str), (c) => c.charCodeAt(0))

  const salt = combined.subarray(0, 16)
  const iv = combined.subarray(16, 32)
  const ciphertext = combined.subarray(32)

  const passwordRaw = new Uint8Array(password.length)
  for (let i = 0; i < password.length; i += 1) {
    passwordRaw[i] = password.charCodeAt(i) & 0xff // eslint-disable-line no-bitwise
  }

  const keyMaterial = await crypto.subtle.importKey('raw', passwordRaw, { name: 'PBKDF2' }, false, [
    'deriveKey',
  ])
  const key = await crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 100000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-CBC', length: 256 },
    false,
    ['decrypt'],
  )

  const decrypted = await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, key, ciphertext)
  return new TextDecoder('utf-8').decode(decrypted)
}

const handleDecryptOk = async ({ encryptedData, passwordRef, modal, t, messageApi }) => {
  if (!passwordRef.value) {
    messageApi.error(t('record.desc.decryptFileDesc'))
    return rejectKeepOpen()
  }

  try {
    modal.update({
      content: (
        <div className={styles.decryptModal}>
          <div className={styles.description}>{t('record.desc.decryptFileDesc')}</div>
          <div className={styles.passwordField}>
            <PasswordInput
              disabled
              value={passwordRef.value}
              placeholder={t('record.desc.decryptFilePlaceholder')}
            />
          </div>
          <div style={{ marginTop: 8, color: '#6b7280', fontSize: 13 }}>
            {t('record.result.decryptingFileDesc')}
          </div>
        </div>
      ),
      okButtonProps: {
        className: classNames('type7', 'w100', 'h38'),
        // loading: true,
        style: { borderColor: '#d1d5db' },
      },
      cancelButtonProps: { className: classNames('w100', 'h38') },
    })

    await new Promise((resolve) => {
      setTimeout(resolve, 50)
    })

    await processImportData(null, t, messageApi, { encryptedData, password: passwordRef.value })

    modal.destroy()
    return Promise.resolve()
  } catch (error) {
    modal.update({
      content: (
        <div className={styles.decryptModal}>
          <div className={styles.description}>{t('record.desc.decryptFileDesc')}</div>
          <div className={styles.passwordField}>
            <PasswordInput
              onChange={(e) => {
                passwordRef.value = e.target.value
              }}
              defaultValue={passwordRef.value}
              placeholder={t('record.desc.decryptFilePlaceholder')}
            />
          </div>
        </div>
      ),
      okButtonProps: {
        className: classNames('type7', 'w100', 'h38'),
        loading: false,
        disabled: false,
      },
      cancelButtonProps: { disabled: false, className: classNames('w100', 'h38') },
    })
    if (
      error?.message === IMPORT_ERROR.CHECKSUM_MISMATCH ||
      error?.message === IMPORT_ERROR.CHECKSUM_MISSING
    ) {
      messageApi.error(t('record.error.corruptedImportFile'))
    } else {
      if (!(error instanceof URIError || error instanceof SyntaxError)) {
        console.error('Decrypt error:', error)
      }
      messageApi.error(t('record.error.decryptFailed'))
    }
    return rejectKeepOpen()
  }
}

const showDecryptModal = (encryptedData, t, messageApi) => {
  const passwordRef = { value: '' }
  const modalRef = { current: null }

  modalRef.current = confirmBase({
    wrapClassName: classNames(styles.decryptModal),
    icon: <img src={exclamationBlackIcon} alt="info" width={22} height={22} />,
    title: <div className={styles.decryptTitle}>{t('record.desc.decryptFileTitle')}</div>,
    width: 480,
    content: (
      <div className={styles.decryptModal}>
        <div className={styles.description}>{t('record.desc.decryptFileDesc')}</div>
        <div className={styles.passwordField}>
          <PasswordInput
            onChange={(e) => {
              passwordRef.value = e.target.value
            }}
            onPressEnter={() => {
              const okBtn = document.querySelector('.ant-modal-confirm-btns .ant-btn-primary')
              if (okBtn) okBtn.click()
            }}
            placeholder={t('record.desc.decryptFilePlaceholder')}
          />
        </div>
      </div>
    ),
    okText: t('common.button.ok'),
    cancelText: t('common.button.cancel'),
    okButtonProps: { className: classNames('type7', 'w100', 'h38') },
    cancelButtonProps: { className: classNames('w100', 'h38') },
    onOk: () =>
      handleDecryptOk({ encryptedData, passwordRef, modal: modalRef.current, t, messageApi }),
  })
}

/**
 * 통합 import: 파일 선택 → 내용에 따라 자동 분기
 * - .enc → Safebox 암호화 백업 복원
 * - .json (Safebox 구조) → Safebox 백업 복원
 * - .json (외부 서비스) → 매핑 모달
 * - .csv → 매핑 모달
 * 매핑 모달이 필요한 경우 onParsed 콜백으로 데이터 전달
 */
const openImportFile = (t, messageApi, onParsed) => {
  openFile(
    null,
    async (url, file) => {
      try {
        const text = await file.text()

        // .csv → 매핑 모달 (외부 import 비활성화 시 차단)
        if (file.name.toLowerCase().endsWith('.csv')) {
          if (!featureFlags.externalImport) {
            messageApi.error(t('record.error.externalImportDisabled'))
            return
          }
          try {
            const parsed = parseCsvRaw(text)
            if (parsed.rows.length === 0) {
              messageApi.error(t('record.error.emptyImportFile'))
              return
            }
            const columns = buildAutoMapping(parsed.headers)
            onParsed({ columns, rows: parsed.rows, totalCount: parsed.rows.length })
          } catch (error) {
            console.error('CSV parse error:', error)
            messageApi.error(t('record.error.importFailed'))
          }
          return
        }

        // JSON 파싱 시도
        let importData = null
        try {
          importData = JSON.parse(text)
        } catch (e) {
          // JSON 실패 → CSV 폴백 시도 (외부 import 비활성화 시 차단)
          if (!featureFlags.externalImport) {
            messageApi.error(t('record.error.externalImportDisabled'))
            return
          }
          try {
            const parsed = parseCsvRaw(text)
            if (parsed.rows.length === 0) {
              messageApi.error(t('record.error.emptyImportFile'))
              return
            }
            const columns = buildAutoMapping(parsed.headers)
            onParsed({ columns, rows: parsed.rows, totalCount: parsed.rows.length })
          } catch (fallbackError) {
            console.error('CSV fallback parse error:', fallbackError)
            messageApi.error(t('record.error.invalidImportFile'))
          }
          return
        }

        if (!importData) {
          messageApi.error(t('record.error.invalidImportFile'))
          return
        }

        // Safebox 암호화 백업 → 비밀번호 입력 → 복원
        if (importData.encrypted === true) {
          showDecryptModal(importData, t, messageApi)
          return
        }

        // Safebox JSON 백업 → 바로 복원
        if (isSafeboxBackup(importData)) {
          await processImportData(unwrapExportData(importData), t, messageApi)
          return
        }

        // Safebox 백업이 아닌 모든 JSON → 매핑 모달 (외부 import 비활성화 시 차단)
        if (!featureFlags.externalImport) {
          messageApi.error(t('record.error.externalImportDisabled'))
          return
        }
        try {
          const flattened = flattenExternalJson(importData)
          if (flattened.rows.length === 0) {
            messageApi.error(t('record.error.emptyImportFile'))
            return
          }
          const columns = buildAutoMapping(flattened.headers)
          onParsed({ columns, rows: flattened.rows, totalCount: flattened.rows.length })
        } catch (error) {
          if (error?.message === 'UNSUPPORTED_JSON_FORMAT') {
            messageApi.error(t('record.error.unsupportedJsonFormat'))
          } else {
            console.error('JSON parse error:', error)
            messageApi.error(t('record.error.importFailed'))
          }
        }
      } catch (error) {
        console.error('Import error:', error)
        messageApi.error(t('record.error.importFailed'))
      }
    },
    featureFlags.externalImport ? '.json,.enc,.csv' : '.json,.enc',
    FILE_SIZE_LIMIT,
    () => {
      messageApi.error(t('record.error.fileSizeLimit'))
    },
  )
}

// ---------- 소스 선택 기반 Import ----------

const fetchWebLoginTemplateId = async (messageApi, t) => {
  try {
    const response = await ResourceTemplateService.getAll({
      params: { search: 'Web Login', lang: 'en', offset: 0, limit: 10 },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    // readonly=true인 기본 템플릿 중 'Web Login' 찾기
    // API는 is_brief=True 응답. lang=en이므로 name은 항상 'Web Login'
    const webLoginTemplate = response.data.find(
      (tpl) => tpl.readonly === true && tpl.name === 'Web Login',
    )

    if (!webLoginTemplate) {
      messageApi.error(t('record.error.webLoginTemplateNotFound'))
      return null
    }

    return webLoginTemplate.id
  } catch (error) {
    console.error('Failed to fetch web login template:', error)
    messageApi.error(t('record.error.templateFetchFailed'))
    return null
  }
}

const openImportFileWithSource = (source, t, messageApi) => {
  // eslint-disable-next-line global-require -- lazy import로 순환 참조 방지
  const parseSafePasswordFile = require('./importers/safePasswordImporter').default
  // eslint-disable-next-line global-require
  const parseGooglePasswordFile = require('./importers/googleCsvImporter').default

  openFile(
    null,
    async (url, file) => {
      try {
        const text = await file.text()

        if (source.key === 'safepassword') {
          const result = parseSafePasswordFile(text)

          if (result.type === 'encrypted') {
            showDecryptModal(result.data, t, messageApi)
          } else if (result.type === 'backup') {
            await processImportData(unwrapExportData(result.data), t, messageApi)
          } else {
            showImportErrorModal(t)
          }
        } else if (source.key === 'google') {
          const result = parseGooglePasswordFile(text)

          if (result.type === 'google') {
            const templateId = await fetchWebLoginTemplateId(messageApi, t)
            if (!templateId) return

            result.records.forEach((r) => { r.template = { id: templateId } })
            await processImportData(result.records, t, messageApi, { skippedRows: result.skippedRows })
          } else {
            showImportErrorModal(t)
          }
        }
      } catch (error) {
        console.error('Import error:', error)
        messageApi.error(t('record.error.importFailed'))
      }
    },
    source.accept,
    FILE_SIZE_LIMIT,
    () => messageApi.error(t('record.error.fileSizeLimit')),
  )
}

export {
  runDarkWebCheckWithAnimation,
  showDarkWebModal,
  validateImportRecord,
  unwrapExportData,
  openImportFile,
  openImportFileWithSource,
  processImportData,
  convertWithMapping,
  executeImportRecords,
  executeImportRecord,
  checkImportQuota,
  fetchDefaultTemplateId,
  fetchWebLoginTemplateId,
  handleDarkWebCheckAfterImport,
  isDarkWebActive,
  reloadMenu,
  decryptForImport,
  IMPORT_ERROR,
  decryptRecords,
  validateTemplateId,
  fetchTemplateSchema,
  fetchTypeList,
  toCustomFields,
  showImportFailedModal,
}
export default openImportFile
